import { useState } from "react";
import {
    Button,
    Checkbox,
    Dialog,
    DialogContent,
    DialogTitle,
    Input,
    Mono,
    OrgAvatar,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    StatusBadge,
    Text,
} from "@athena/ui";
import { ChevronDown, ChevronRight, Download, Search } from "lucide-react";
import { cn } from "@athena/ui/utils";
import { AssignmentProgress } from "./AssignmentProgress";
import { UserTable, userColumns } from "@/components/tables/UserTable";
import { useTranslation } from "react-i18next";
import { useListView } from "@/hooks/useListView";
import { ListViewControls } from "@/components/tables/ListViewControls";
import { exportCsv } from "@/lib/exportCsv";
import { formatDateTime } from "../../../../global/formatHelpers";

type AssignmentStatus = "completed" | "in_progress" | "not_started";

interface UserAssignment {
    id: string;
    userId: string;
    name: string;
    email: string;
    initials: string;
    organizationId: string | null;
    organizationName: string;
    parentOrganizationId: string | null;
    parentOrganizationName: string;
    organization: string;
    dueDate: string;
    progress: string;
    status: AssignmentStatus;
    currentStep: number;
    totalSteps: number;
    assignedAt: string;
    completedAt?: string | null;
}

interface AssignmentUsersProps {
    assignments: UserAssignment[];
    /** Refetches the course's assignment data after an edit or delete. */
    onAssignmentsChanged?: () => void;
}

/** The section a learner with no organization membership falls into. */
const NO_ORGANIZATION_ID = "none";
const NO_ORGANIZATION_LABEL = "No organization";

const CSV_HEADERS = [
    "Name",
    "Email",
    "Organization",
    "Sub-organization",
    "Due Date",
    "Status",
    "Current Step",
    "Total Steps",
    "Assigned At",
    "Completed At",
];

/**
 * How a status reads in the file. Separate from the table's `t(...)` labels on purpose: an export
 * is read in a spreadsheet, where a value that changes with the operator's language is a value
 * nobody can filter on.
 */
const CSV_STATUS_LABEL: Record<AssignmentStatus, string> = {
    completed: "Completed",
    in_progress: "In progress",
    not_started: "Not started",
};

const assignmentToCsvRow = (a: UserAssignment): string[] => [
    a.name,
    a.email,
    // Root first, sub second — the same split the users and org-members exports use, so the three
    // files line up in a pivot table instead of one of them carrying "Parent — Child" in one cell.
    a.parentOrganizationName || a.organizationName,
    a.parentOrganizationName ? a.organizationName : "",
    a.dueDate,
    CSV_STATUS_LABEL[a.status],
    String(a.currentStep),
    String(a.totalSteps),
    formatDateTime(a.assignedAt),
    a.completedAt ? formatDateTime(a.completedAt) : "",
];

/**
 * Column widths, shared by every section's table.
 *
 * Each organization renders its own `<table>`, and a table sizes its columns from its own content —
 * so three sections with different names in them came out with three different Due columns, sitting
 * under a header bar that spans all of them. Naming every width, and `table-fixed` to honor them, is
 * what makes the sections one grid.
 *
 * Shares of the table rather than pixel widths, summing to 100. A table fills its container either
 * way; what differs is where the difference goes. Fixed widths that fall short leave a remainder,
 * and `table-fixed` spreads it over every column — the checkbox column included, which is what put
 * the gap on the left edge. Proportions have no remainder to spread, so each column keeps its share
 * of whatever width the table has.
 *
 * `min-w-12` is the one exception: 4% of a narrow table is less than a checkbox, and a column may be
 * as wide as its share says but not narrower than the thing standing in it.
 */
const COLUMN_WIDTH = {
    select: "w-[4%] min-w-12",
    identity: "w-[36%]",
    due: "w-[20%]",
    status: "w-[20%]",
    progress: "w-[20%]",
} as const;

/** One organization's rows and its completion, at either level of the tree. */
interface OrgSection {
    id: string;
    name: string;
    assignments: UserAssignment[];
    completed: number;
    completionPct: number;
}

/**
 * A top-level organization: its own members, its sub-organizations, and the roll-up over both.
 *
 * `assignments` is everything beneath it, which is what the header counts and what its checkbox
 * reaches. `directAssignments` is only the people held by the parent itself — an organization can
 * have both, and those rows belong above its sub-organizations rather than inside one of them.
 */
interface ParentSection extends OrgSection {
    directAssignments: UserAssignment[];
    children: OrgSection[];
}

const withCompletion = <T extends OrgSection>(section: T): T => {
    const completed = section.assignments.filter((a) => a.status === "completed").length;
    return {
        ...section,
        completed,
        completionPct: Math.round((completed / section.assignments.length) * 100),
    };
};

/**
 * The assignments as the organization tree, ordered by name at both levels.
 *
 * Alphabetical rather than by size, unlike the overview's completion list: this is the section an
 * operator goes looking for by name, and a list that reorders itself as people finish the course
 * is one they have to re-scan every visit.
 *
 * A learner's membership names one organization, which may be a sub-organization; the parent is the
 * level above it, or the organization itself when there is nothing above. That is why a top-level
 * organization and a parent are the same kind of thing here — Georgetown has no children and holds
 * its people directly, Caron holds none of its own and has two campuses.
 */
const groupByOrganization = (assignments: UserAssignment[]): ParentSection[] => {
    const parents = new Map<string, ParentSection>();

    for (const assignment of assignments) {
        const orgId = assignment.organizationId ?? NO_ORGANIZATION_ID;
        const parentId = assignment.parentOrganizationId ?? orgId;
        const parent = parents.get(parentId) ?? {
            id: parentId,
            name: assignment.parentOrganizationName || assignment.organizationName || NO_ORGANIZATION_LABEL,
            assignments: [],
            directAssignments: [],
            children: [],
            completed: 0,
            completionPct: 0,
        };
        parent.assignments.push(assignment);

        if (assignment.parentOrganizationId) {
            const child = parent.children.find((c) => c.id === orgId) ?? {
                id: orgId,
                name: assignment.organizationName || NO_ORGANIZATION_LABEL,
                assignments: [],
                completed: 0,
                completionPct: 0,
            };
            child.assignments.push(assignment);
            if (!parent.children.includes(child)) parent.children.push(child);
        } else {
            parent.directAssignments.push(assignment);
        }

        parents.set(parentId, parent);
    }

    return [...parents.values()]
        .map((parent) => ({
            ...withCompletion(parent),
            children: parent.children.map(withCompletion).sort((a, b) => a.name.localeCompare(b.name)),
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
};

interface OrgSectionHeaderProps {
    section: OrgSection;
    collapsed: boolean;
    /** Every one of its rows is picked, which is also what its checkbox does when it is not. */
    selected: boolean;
    /** A sub-organization, stepped in under its parent. */
    nested?: boolean;
    onToggleCollapsed: () => void;
    onToggleSelected: () => void;
}

/**
 * An organization's bar: pick it, open it, and how far along it is.
 *
 * The checkbox sits beside the collapse control rather than inside it: a button cannot contain
 * another button, and picking an organization should not also open or close it.
 */
const OrgSectionHeader = ({
    section,
    collapsed,
    selected,
    nested = false,
    onToggleCollapsed,
    onToggleSelected,
}: OrgSectionHeaderProps) => (
    <div className={cn("flex items-center gap-3 px-4 py-3", nested && "pl-10")}>
        <Checkbox
            checked={selected}
            onCheckedChange={onToggleSelected}
            aria-label={`Select all in ${section.name}`}
            className="shrink-0"
        />
        <button
            type="button"
            onClick={onToggleCollapsed}
            aria-expanded={!collapsed}
            className="hover:bg-muted/50 -my-1 flex min-w-0 flex-1 items-center gap-3 rounded-md py-1 text-left"
        >
            {collapsed ? (
                <ChevronRight className="text-muted-foreground h-4 w-4 shrink-0" />
            ) : (
                <ChevronDown className="text-muted-foreground h-4 w-4 shrink-0" />
            )}
            <OrgAvatar id={section.id} fallback={section.name.slice(0, 2).toUpperCase()} size="sm" />
            <Text as="span" size="sm" weight={nested ? "medium" : "semibold"} className="truncate">
                {section.name}
            </Text>
            <Mono as="span" muted size="xs" className="ml-auto shrink-0">
                {section.assignments.length} assigned · {section.completed} completed · {section.completionPct}%
            </Mono>
        </button>
    </div>
);

export function AssignmentUsers({ assignments, onAssignmentsChanged }: AssignmentUsersProps) {
    const { t } = useTranslation();

    const [
        search,
        setSearch,
    ] = useState<string>("");
    const [
        orgFilter,
        setOrgFilter,
    ] = useState("all");
    const [
        selectedAssignment,
        setSelectedAssignment,
    ] = useState<UserAssignment | null>(null);
    const [
        collapsedOrgIds,
        setCollapsedOrgIds,
    ] = useState<ReadonlySet<string>>(new Set());
    /*
     * What the export will contain, when it is not everything.
     *
     * Held by assignment id and kept across filter changes: a selection is something the operator
     * said out loud, and narrowing the search afterwards to find the next person to add should not
     * quietly drop the ones already picked. The button carries the count so the file is never a
     * surprise — that number, not the rows on screen, is what lands in it.
     */
    const [
        selectedIds,
        setSelectedIds,
    ] = useState<ReadonlySet<string>>(new Set());

    const orgs = Array.from(new Set(assignments.map((a) => a.organization).filter(Boolean))).sort();
    const filtered = assignments.filter((a) => {
        const matchesSearch =
            a.email.toLowerCase().includes(search.toLowerCase()) || a.name.toLowerCase().includes(search.toLowerCase());
        const matchesOrg = orgFilter === "all" || a.organization === orgFilter;
        return matchesSearch && matchesOrg;
    });

    /* Scroll or pages, the reader's choice, portal-wide — see `useListView`. */
    const view = useListView(filtered);
    const visibleAssignmentIds = new Set(view.rows.map((a) => a.id));
    const groups = groupByOrganization(filtered);

    const toUserRow = (a: UserAssignment) => ({
        // The person seeds the avatar; the assignment identifies the row.
        id: a.userId,
        rowId: a.id,
        name: a.name,
        email: a.email,
        organizationName: a.organization,
        dueDate: a.dueDate,
        progress: a.progress,
    });

    const statusOf = (assignmentId?: string) => filtered.find((a) => a.id === assignmentId)?.status;

    const exportSet = selectedIds.size > 0 ? assignments.filter((a) => selectedIds.has(a.id)) : filtered;

    const handleExportCsv = () => exportCsv("course-assignments", CSV_HEADERS, exportSet.map(assignmentToCsvRow));

    const toggleOrg = (orgId: string) =>
        setCollapsedOrgIds((prev) => {
            const next = new Set(prev);
            if (!next.delete(orgId)) next.add(orgId);
            return next;
        });

    const toggleAssignment = (assignmentId: string) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (!next.delete(assignmentId)) next.add(assignmentId);
            return next;
        });

    const toggleGroup = (groupAssignments: UserAssignment[], selectAll: boolean) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            for (const assignment of groupAssignments) {
                if (selectAll) next.add(assignment.id);
                else next.delete(assignment.id);
            }
            return next;
        });

    const columns = [
        {
            key: "select",
            // The select-all for a group lives in its section header, where it is reachable whether
            // or not the section is open — so this column's header is only the space it occupies.
            header: "",
            headerClassName: COLUMN_WIDTH.select,
            className: COLUMN_WIDTH.select,
            cell: (user: { rowId?: string; name?: string | null; email: string }) => (
                // A click on the checkbox is a click on the checkbox. Without this it also opens
                // the row's assignment dialog, which is the last thing somebody ticking a list wants.
                <span onClick={(event) => event.stopPropagation()}>
                    <Checkbox
                        checked={user.rowId !== undefined && selectedIds.has(user.rowId)}
                        onCheckedChange={() => user.rowId && toggleAssignment(user.rowId)}
                        aria-label={`Select ${user.name || user.email}`}
                    />
                </span>
            ),
        },
        { ...userColumns.identity, headerClassName: COLUMN_WIDTH.identity, className: COLUMN_WIDTH.identity },
        {
            ...userColumns.text("due", t("common.fields.due"), (u) => u.dueDate),
            headerClassName: COLUMN_WIDTH.due,
            className: COLUMN_WIDTH.due,
        },
        {
            ...userColumns.badge("assignmentStatus", t("common.fields.status"), (u) => {
                const status = statusOf(u.rowId);
                if (status === "completed")
                    return (
                        <StatusBadge dot size="ghost" variant="success">
                            {t("common.fields.completed")}
                        </StatusBadge>
                    );
                if (status === "in_progress")
                    return (
                        <StatusBadge dot size="ghost" variant="warning">
                            {t("common.fields.inProgress")}
                        </StatusBadge>
                    );
                return (
                    <StatusBadge dot size="ghost" variant="muted">
                        {t("common.fields.notStarted")}
                    </StatusBadge>
                );
            }),
            headerClassName: COLUMN_WIDTH.status,
            className: COLUMN_WIDTH.status,
        },
        {
            ...userColumns.text("progress", t("common.fields.progress"), (u) => u.progress),
            headerClassName: COLUMN_WIDTH.progress,
            className: COLUMN_WIDTH.progress,
        },
    ];

    /** Whether a section's checkbox reads as ticked — every row under it is picked. */
    const isWhollySelected = (sectionAssignments: UserAssignment[]) =>
        sectionAssignments.every((a) => selectedIds.has(a.id));

    /**
     * One section's rows. The card around it is the organization's, so the table drops the border
     * and radius it would otherwise draw inside that one, keeping only the rule under the header.
     */
    const sectionTable = (sectionAssignments: UserAssignment[]) => (
        <UserTable
            columns={columns}
            rows={sectionAssignments.map(toUserRow)}
            rowKey={(user) => user.rowId ?? user.id}
            rowSelected={(user) => user.rowId !== undefined && selectedIds.has(user.rowId)}
            onRowClick={(user) => openAssignment(user.rowId)}
            className="table-fixed"
            wrapperClassName="rounded-none border-x-0 border-b-0"
            filtered
            noMatches={t("common.states.noUsersMatchFilter")}
        />
    );

    const openAssignment = (rowId?: string) => {
        const assignment = filtered.find((a) => a.id === rowId);
        if (assignment) setSelectedAssignment(assignment);
    };

    return (
        <div className="flex flex-col gap-4">
            <div className="flex gap-3">
                <div className="relative flex-1">
                    <Search className="text-muted-foreground absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2" />
                    <Input
                        placeholder="Search by name or email..."
                        className="bg-card h-9 pl-9 text-sm"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        type="text"
                    />
                </div>
                <Select value={orgFilter} onValueChange={setOrgFilter}>
                    <SelectTrigger className="bg-card h-9 w-100 text-sm">
                        <SelectValue placeholder="All organizations" />
                    </SelectTrigger>
                    <SelectContent className="bg-card">
                        <SelectItem value="all">{t("common.fields.allOrgs")}</SelectItem>
                        {orgs.map((o) => (
                            <SelectItem key={o} value={o}>
                                {o}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                {selectedIds.size > 0 && (
                    <Button variant="ghost" size="sm" className="h-9 w-auto" onClick={() => setSelectedIds(new Set())}>
                        Clear selection
                    </Button>
                )}
                <Button
                    variant="outline"
                    size="sm"
                    className="flex h-9 w-auto items-center gap-1.5"
                    onClick={handleExportCsv}
                    disabled={exportSet.length === 0}
                >
                    <Download className="h-3.5 w-3.5" />
                    {/* The count, once there is a selection, because that is what separates this
                        button from the one that exports the whole screen. */}
                    {selectedIds.size > 0 ? `Export ${selectedIds.size} selected` : t("admin.exportCsv")}
                </Button>
            </div>

            <ListViewControls view={view} noun="people" count={`${view.from}–${view.to} of ${filtered.length}`} />

            {filtered.length === 0 ? (
                <UserTable
                    columns={columns}
                    rows={[]}
                    rowKey={(user) => user.rowId ?? user.id}
                    className="table-fixed"
                    filtered
                    noMatches={t("common.states.noUsersMatchFilter")}
                />
            ) : (
                groups.map((parent) => {
                    const directRows = parent.directAssignments.filter((a) => visibleAssignmentIds.has(a.id));
                    const children = parent.children
                        .map((child) => ({
                            child,
                            rows: child.assignments.filter((a) => visibleAssignmentIds.has(a.id)),
                        }))
                        .filter(({ rows }) => rows.length > 0);
                    // A section whose every row fell off this page is not an empty organization,
                    // so it is not drawn as one.
                    if (directRows.length === 0 && children.length === 0) return null;
                    const collapsed = collapsedOrgIds.has(parent.id);

                    return (
                        <div key={parent.id} className="border-border bg-card overflow-hidden rounded-lg border">
                            <OrgSectionHeader
                                section={parent}
                                collapsed={collapsed}
                                selected={isWhollySelected(parent.assignments)}
                                onToggleCollapsed={() => toggleOrg(parent.id)}
                                onToggleSelected={() =>
                                    toggleGroup(parent.assignments, !isWhollySelected(parent.assignments))
                                }
                            />
                            {!collapsed && (
                                <>
                                    {/* An organization's own members, above its sub-organizations
                                        rather than inside one of them. Most have either these or
                                        children, but nothing stops an organization having both. */}
                                    {directRows.length > 0 && sectionTable(directRows)}
                                    {children.map(({ child, rows }) => {
                                        const childCollapsed = collapsedOrgIds.has(child.id);
                                        return (
                                            <div key={child.id} className="border-border border-t">
                                                <OrgSectionHeader
                                                    section={child}
                                                    collapsed={childCollapsed}
                                                    selected={isWhollySelected(child.assignments)}
                                                    nested
                                                    onToggleCollapsed={() => toggleOrg(child.id)}
                                                    onToggleSelected={() =>
                                                        toggleGroup(
                                                            child.assignments,
                                                            !isWhollySelected(child.assignments),
                                                        )
                                                    }
                                                />
                                                {!childCollapsed && sectionTable(rows)}
                                            </div>
                                        );
                                    })}
                                </>
                            )}
                        </div>
                    );
                })
            )}

            <Dialog
                open={selectedAssignment !== null}
                onOpenChange={(open) => {
                    if (!open) setSelectedAssignment(null);
                }}
            >
                <DialogContent className="flex h-[70vh] max-w-4xl flex-col gap-0 p-0">
                    <DialogTitle className="border-border border-b px-6 py-4 text-base font-semibold">
                        <Mono>{t("common.fields.assignmentDetails")}</Mono>
                    </DialogTitle>
                    <div className="min-h-0 flex-1">
                        {selectedAssignment && (
                            <AssignmentProgress
                                assignment={selectedAssignment}
                                onDueDateUpdated={(dueDate) => {
                                    setSelectedAssignment((prev) =>
                                        prev ? { ...prev, dueDate: new Date(dueDate).toLocaleDateString() } : prev,
                                    );
                                    onAssignmentsChanged?.();
                                }}
                                onDeleted={() => {
                                    setSelectedAssignment(null);
                                    onAssignmentsChanged?.();
                                }}
                            />
                        )}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
